/**
 * GapSummary - Current → Target with actionable gap items
 *
 * Displays a summary of the gap between current and target maturity levels,
 * including a list of capabilities needed to close the gap.
 */

import React, { useMemo } from 'react';
import {
  ArrowRight,
  CheckCircle,
  Circle,
  AlertTriangle,
  TrendingUp,
  ListChecks,
  Lightbulb,
  ExternalLink
} from 'lucide-react';
import { MATURITY_LEVELS } from '../../../../config/zeroTrustConstants';
import MaturityLevelBadge, { MaturityProgressBar } from './MaturityLevelBadge';
import './GapSummary.css';

export default function GapSummary({
  currentLevel,
  targetLevel,
  missingCapabilities = [], // Array of { questionText, impliesLevel, priority }
  recommendations = [], // Array of { text, priority, link }
  functionName = '',
  onViewDetails,
  animated = true
}) {
  const currentIdx = MATURITY_LEVELS.findIndex(m => m.key === currentLevel);
  const targetIdx = MATURITY_LEVELS.findIndex(m => m.key === targetLevel);
  const levelGap = targetIdx - currentIdx;
  const currentData = MATURITY_LEVELS.find(m => m.key === currentLevel);
  const targetData = MATURITY_LEVELS.find(m => m.key === targetLevel);

  // Group missing capabilities by the level they imply
  const capabilitiesByLevel = useMemo(() => {
    const grouped = {};
    MATURITY_LEVELS.forEach(level => {
      grouped[level.key] = missingCapabilities.filter(c => c.impliesLevel === level.key);
    });
    return grouped;
  }, [missingCapabilities]);

  // Calculate priority counts
  const priorityCounts = useMemo(() => {
    return {
      high: missingCapabilities.filter(c => c.priority === 'high').length,
      medium: missingCapabilities.filter(c => c.priority === 'medium').length,
      low: missingCapabilities.filter(c => c.priority === 'low').length
    };
  }, [missingCapabilities]);

  const hasGap = levelGap > 0;
  const noGap = levelGap === 0;
  const negativeGap = levelGap < 0;

  return (
    <div className={`gs-container ${animated ? 'gs-container--animated' : ''}`}>
      {/* Header with level comparison */}
      <div className="gs-header">
        {functionName && (
          <h3 className="gs-function-name">{functionName}</h3>
        )}

        <div className="gs-levels">
          <div className="gs-level gs-level--current">
            <span className="gs-level-label">Current</span>
            <MaturityLevelBadge level={currentLevel} size="md" animated />
          </div>

          <div className="gs-arrow">
            <ArrowRight size={20} />
          </div>

          <div className="gs-level gs-level--target">
            <span className="gs-level-label">Target</span>
            <MaturityLevelBadge level={targetLevel} size="md" variant="outline" animated />
          </div>
        </div>

        <MaturityProgressBar
          currentLevel={currentLevel}
          targetLevel={targetLevel}
          showLabels
          animated
        />
      </div>

      {/* Gap Status */}
      <div className={`gs-status gs-status--${hasGap ? 'gap' : noGap ? 'complete' : 'warning'}`}>
        {noGap && (
          <>
            <CheckCircle size={20} />
            <span>Target achieved! You're at your target maturity level.</span>
          </>
        )}
        {hasGap && (
          <>
            <TrendingUp size={20} />
            <span>
              {levelGap} level{levelGap > 1 ? 's' : ''} gap identified •{' '}
              {missingCapabilities.length} capabilities to address
            </span>
          </>
        )}
        {negativeGap && (
          <>
            <AlertTriangle size={20} />
            <span>Target is below your current level. Consider setting a higher target.</span>
          </>
        )}
      </div>

      {/* Missing Capabilities */}
      {hasGap && missingCapabilities.length > 0 && (
        <div className="gs-capabilities">
          <div className="gs-capabilities-header">
            <h4 className="gs-capabilities-title">
              <ListChecks size={18} />
              Capabilities to Implement
            </h4>
            <div className="gs-priority-counts">
              {priorityCounts.high > 0 && (
                <span className="gs-priority-badge gs-priority-badge--high">
                  {priorityCounts.high} High
                </span>
              )}
              {priorityCounts.medium > 0 && (
                <span className="gs-priority-badge gs-priority-badge--medium">
                  {priorityCounts.medium} Medium
                </span>
              )}
              {priorityCounts.low > 0 && (
                <span className="gs-priority-badge gs-priority-badge--low">
                  {priorityCounts.low} Low
                </span>
              )}
            </div>
          </div>

          <div className="gs-capability-list">
            {MATURITY_LEVELS.map(level => {
              const caps = capabilitiesByLevel[level.key];
              if (!caps || caps.length === 0) return null;

              return (
                <div
                  key={level.key}
                  className="gs-capability-group"
                  style={{ '--level-color': level.color }}
                >
                  <div className="gs-capability-group-header">
                    <MaturityLevelBadge level={level.key} size="sm" variant="subtle" />
                    <span className="gs-capability-count">{caps.length} items</span>
                  </div>
                  <ul className="gs-capability-items">
                    {caps.map((cap, idx) => (
                      <li
                        key={idx}
                        className={`gs-capability-item gs-capability-item--${cap.priority || 'medium'}`}
                        style={{ animationDelay: `${idx * 0.05}s` }}
                      >
                        <Circle size={8} className="gs-capability-bullet" />
                        <span>{cap.questionText}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="gs-recommendations">
          <h4 className="gs-recommendations-title">
            <Lightbulb size={18} />
            Recommendations
          </h4>
          <ul className="gs-recommendations-list">
            {recommendations.map((rec, idx) => (
              <li
                key={idx}
                className="gs-recommendation-item"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <span className="gs-recommendation-text">{rec.text}</span>
                {rec.link && (
                  <a
                    href={rec.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gs-recommendation-link"
                  >
                    Learn more <ExternalLink size={12} />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* View Details Button */}
      {onViewDetails && (
        <button className="gs-details-btn" onClick={onViewDetails}>
          View Detailed Gap Analysis
          <ArrowRight size={16} />
        </button>
      )}
    </div>
  );
}

/**
 * GapSummaryCompact - Minimal gap indicator
 */
export function GapSummaryCompact({
  currentLevel,
  targetLevel,
  gapCount = 0,
  onClick,
  animated = true
}) {
  const currentIdx = MATURITY_LEVELS.findIndex(m => m.key === currentLevel);
  const targetIdx = MATURITY_LEVELS.findIndex(m => m.key === targetLevel);
  const levelGap = targetIdx - currentIdx;

  if (levelGap <= 0) {
    return (
      <div className={`gs-compact gs-compact--complete ${animated ? 'gs-compact--animated' : ''}`}>
        <CheckCircle size={16} />
        <span>Target achieved</span>
      </div>
    );
  }

  return (
    <button
      className={`gs-compact gs-compact--gap ${animated ? 'gs-compact--animated' : ''}`}
      onClick={onClick}
    >
      <TrendingUp size={16} />
      <span>
        {levelGap} level{levelGap > 1 ? 's' : ''} gap
        {gapCount > 0 && ` • ${gapCount} items`}
      </span>
      <ArrowRight size={14} />
    </button>
  );
}

/**
 * GapProgressRing - Circular progress showing gap closure
 */
export function GapProgressRing({
  achieved,
  total,
  size = 80,
  strokeWidth = 8,
  animated = true
}) {
  const percentage = total > 0 ? (achieved / total) * 100 : 0;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div
      className={`gs-ring ${animated ? 'gs-ring--animated' : ''}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size}>
        <circle
          className="gs-ring-bg"
          stroke="var(--color-surface-hover)"
          fill="none"
          strokeWidth={strokeWidth}
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="gs-ring-progress"
          stroke="var(--zt-optimal)"
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%'
          }}
        />
      </svg>
      <div className="gs-ring-text">
        <span className="gs-ring-value">{achieved}</span>
        <span className="gs-ring-total">/{total}</span>
      </div>
    </div>
  );
}
